import { FileText, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Лимиты «Параметров встречи». Те же значения проверяет Public API v2
 * (422 сверх лимита) и task-flow перед вставкой в промпт ИИ.
 */
export const SPEAKER_NAMES_MAX_LENGTH = 1000;
export const CONTEXT_MAX_LENGTH = 4000;

interface MeetingParamsFieldsProps {
  /** Префикс id полей: на странице два экземпляра (десктоп и мобильный шаг). */
  idPrefix?: string;
  disabled?: boolean;
  speakerNames: string;
  onSpeakerNamesChange: (value: string) => void;
  meetingContext: string;
  onMeetingContextChange: (value: string) => void;
}

/** Доля лимита, с которой счётчик подсвечивается предупреждением. */
const NEAR_LIMIT_RATIO = 0.9;

const counterId = (fieldId: string) => `${fieldId}-counter`;
const hintId = (fieldId: string) => `${fieldId}-hint`;

/** id подсказки и, если поле заполнено, счётчика — для aria-describedby. */
function describedBy(fieldId: string, value: string) {
  return value.length > 0 ? `${hintId(fieldId)} ${counterId(fieldId)}` : hintId(fieldId);
}

/**
 * Подсказка на всю ширину и короткий счётчик «N/лимит» справа от неё.
 * Счётчик виден только у заполненного поля, с 90 % лимита подсвечен.
 */
function FieldHint({
  fieldId,
  hint,
  value,
  limit,
}: {
  fieldId: string;
  hint: string;
  value: string;
  limit: number;
}) {
  const nearLimit = value.length >= limit * NEAR_LIMIT_RATIO;
  return (
    <div className="mt-1 flex items-baseline gap-2 text-[11px] leading-snug text-muted-foreground/80">
      <p id={hintId(fieldId)} className="flex-1 min-w-0">
        {hint}
      </p>
      {value.length > 0 && (
        <span
          id={counterId(fieldId)}
          aria-live="polite"
          data-near-limit={nearLimit || undefined}
          className={cn(
            "shrink-0 tabular-nums",
            nearLimit && "font-medium text-amber-600 dark:text-amber-400"
          )}
        >
          {value.length}/{limit}
        </span>
      )}
    </div>
  );
}

export function MeetingParamsFields({
  idPrefix = "meeting",
  disabled = false,
  speakerNames,
  onSpeakerNamesChange,
  meetingContext,
  onMeetingContextChange,
}: MeetingParamsFieldsProps) {
  const speakersId = `${idPrefix}-speaker-names`;
  const contextId = `${idPrefix}-context`;
  const labelClassName = "text-xs text-muted-foreground flex items-center gap-1.5 mb-1 font-medium";

  return (
    <>
      <div>
        <label htmlFor={speakersId} className={labelClassName}>
          <Users className="w-3.5 h-3.5 text-brand-500" aria-hidden="true" />
          Участники
        </label>
        <Input
          id={speakersId}
          value={speakerNames}
          maxLength={SPEAKER_NAMES_MAX_LENGTH}
          onChange={(e) => onSpeakerNamesChange(e.target.value.slice(0, SPEAKER_NAMES_MAX_LENGTH))}
          disabled={disabled}
          aria-describedby={describedBy(speakersId, speakerNames)}
          placeholder="Арсений Иванов, Петр Сидоров"
          className="text-xs sm:text-sm h-9 bg-background"
        />
        <FieldHint
          fieldId={speakersId}
          hint="Через запятую — так имена в протоколе будут написаны верно."
          value={speakerNames}
          limit={SPEAKER_NAMES_MAX_LENGTH}
        />
      </div>
      <div>
        <label htmlFor={contextId} className={labelClassName}>
          <FileText className="w-3.5 h-3.5 text-brand-500" aria-hidden="true" />
          Тема встречи
        </label>
        <Textarea
          id={contextId}
          value={meetingContext}
          maxLength={CONTEXT_MAX_LENGTH}
          onChange={(e) => onMeetingContextChange(e.target.value.slice(0, CONTEXT_MAX_LENGTH))}
          disabled={disabled}
          aria-describedby={describedBy(contextId, meetingContext)}
          placeholder="Тема встречи, договоренности или ключевые термины..."
          className="text-xs sm:text-sm min-h-[60px] bg-background"
          rows={2}
        />
        <FieldHint
          fieldId={contextId}
          hint="Учитывается при составлении протокола, задач и резюме."
          value={meetingContext}
          limit={CONTEXT_MAX_LENGTH}
        />
      </div>
    </>
  );
}
